import React, { useState,useEffect  } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Eye, Trash2, CheckCircle, XCircle, ClipboardList } from 'lucide-react';
import toast from 'react-hot-toast';
import { mrnApi, purchaseOrdersApi, vendorsApi } from '../api/api';

const today = () => new Date().toISOString().split('T')[0];

/* ── View Modal ─────────────────────────────────────────────── */
function ViewMRN({ mrnId, onClose }) {
  const { data: mrn, isLoading } = useQuery({
    queryKey: ['mrn', mrnId],
    queryFn: () => mrnApi.getOne(mrnId),
  });
  const qc = useQueryClient();
  const statusMut = useMutation({
    mutationFn: ({ status }) => mrnApi.updateStatus(mrnId, { status }),
    onSuccess: (d) => {
      qc.invalidateQueries(['mrns']); qc.invalidateQueries(['items']);
      qc.invalidateQueries(['dashboard']); qc.invalidateQueries(['mrn', mrnId]);
      toast.success(`MRN ${d.status === 'approved' ? 'approved — stock updated' : 'rejected'}`);
    },
    onError: (e) => toast.error(e.error || 'Failed'),
  });

  if (isLoading) return (
    <div className="modal-overlay"><div className="modal modal-lg" style={{ padding: 40 }}><div className="spinner" /></div></div>
  );

  const statusColor = { draft: 'var(--amber)', approved: 'var(--accent)', rejected: 'var(--red)' };
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg" style={{ width: 780 }}>
        <div className="modal-header">
          <ClipboardList size={16} />
          <h2>MRN — {mrn.mrn_number}</h2>
          <span className={`badge badge-${mrn.status}`} style={{ marginLeft: 6 }}>{mrn.status.toUpperCase()}</span>
          <div style={{ flex: 1 }} />
          {mrn.status === 'draft' && (
            <>
              <button className="btn btn-sm" style={{ background: 'var(--accent-light)', color: 'var(--accent-text)', border: 'none' }}
                onClick={() => { if (window.confirm('Approve this MRN? Stock will be updated.')) statusMut.mutate({ status: 'approved' }); }}>
                <CheckCircle size={13} /> Approve
              </button>
              <button className="btn btn-sm btn-danger"
                onClick={() => { if (window.confirm('Reject this MRN?')) statusMut.mutate({ status: 'rejected' }); }}>
                <XCircle size={13} /> Reject
              </button>
            </>
          )}
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* Header info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20, padding: 16, background: 'var(--gray-50)', borderRadius: 8 }}>
            <div><div className="form-label">MRN Number</div><strong className="mono">{mrn.mrn_number}</strong></div>
            <div><div className="form-label">Against PO</div><strong className="mono">{mrn.po_number}</strong></div>
            <div><div className="form-label">Vendor</div><strong>{mrn.vendor_name}</strong></div>
            <div><div className="form-label">Received Date</div>{fmtDate(mrn.received_date)}</div>
            <div><div className="form-label">Received By</div>{mrn.received_by || '—'}</div>
            <div><div className="form-label">Total Value</div><strong className="amount">₹{Number(mrn.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></div>
          </div>

          {/* Items table */}
          <div className="section-label">Received Items</div>
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Item</th><th>PO Qty</th><th>Received</th><th>Rejected</th><th style={{ color: 'var(--accent)' }}>Accepted</th><th>Unit Price</th><th>Value</th><th>Batch</th>
              </tr></thead>
              <tbody>
                {(mrn.items || []).map((it, i) => (
                  <tr key={i}>
                    <td><div style={{ fontWeight: 500 }}>{it.item_name}</div><div className="mono" style={{ fontSize: 11, color: 'var(--gray-400)' }}>{it.item_code}</div></td>
                    <td>{it.ordered_qty} {it.unit}</td>
                    <td>{it.received_qty}</td>
                    <td style={{ color: it.rejected_qty > 0 ? 'var(--red)' : undefined }}>{it.rejected_qty}</td>
                    <td style={{ fontWeight: 600, color: 'var(--accent)' }}>{it.accepted_qty}</td>
                    <td className="amount">₹{Number(it.unit_price).toLocaleString('en-IN')}</td>
                    <td className="amount">₹{Number(it.total_price || 0).toLocaleString('en-IN')}</td>
                    <td style={{ fontSize: 12 }}>{it.batch_no || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td colSpan={6} style={{ padding: '10px 18px', textAlign: 'right', fontWeight: 600 }}>Total Accepted Value</td>
                  <td colSpan={2} style={{ padding: '10px 18px', fontWeight: 700, fontFamily: 'var(--mono)' }}>₹{Number(mrn.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {mrn.remarks && <div style={{ marginTop: 14 }}><div className="form-label">Remarks</div><p style={{ fontSize: 13 }}>{mrn.remarks}</p></div>}
        </div>
        <div className="modal-footer"><button className="btn" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

/* ── Create MRN Modal ───────────────────────────────────────── */
function CreateMRN({ onClose }) {
  const qc = useQueryClient();
  const [step, setStep]     = useState(1); // 1=select PO, 2=fill items
  const [selectedPO, setPO] = useState(null);
  const [mrnNum, setMrnNum] = useState('');
  const [form, setForm]     = useState({ received_date: today(), received_by: '', remarks: '' });
  const [lines, setLines]   = useState([]);

  const { data: pos = [] } = useQuery({ queryKey: ['pos-for-mrn'], queryFn: () => purchaseOrdersApi.getAll({ status: '' }) });
  const openPOs = pos.filter(p => !['cancelled', 'received'].includes(p.status));

  const { data: poItems = [], isLoading: loadingItems } = useQuery({
    queryKey: ['po-items', selectedPO?.id],
    queryFn: () => mrnApi.getPoItems(selectedPO.id),
    enabled: !!selectedPO,
    
    // onSuccess: (items) => {
    //    console.log("PO Items:", items);
    //   setLines(items.map(it => ({
    //     item_id: it.item_id, po_item_id: it.id,
    //     item_name: it.item_name, item_code: it.item_code, unit: it.unit,
    //     ordered_qty: it.quantity, already_received: it.received_qty || 0,
    //     remaining_qty: Math.max(0, it.quantity - (it.received_qty || 0)),
    //     received_qty: Math.max(0, it.quantity - (it.received_qty || 0)),
    //     rejected_qty: 0, unit_price: it.unit_price, batch_no: '', expiry_date: '', remarks: '',
    //   })));
    // }


  });
useEffect(() => {
  if (!poItems || poItems.length === 0) return;

  console.log("PO ITEMS:", poItems);

  setLines(poItems.map(it => ({
    item_id: it.item_id,
    po_item_id: it.id,
    item_name: it.item_name,
    item_code: it.item_code,
    unit: it.unit,
    ordered_qty: Number(it.quantity),
    already_received: Number(it.received_qty || 0),
    remaining_qty: Math.max(0, Number(it.quantity) - Number(it.received_qty || 0)),
    received_qty: Math.max(0, Number(it.quantity) - Number(it.received_qty || 0)),
    rejected_qty: 0,
    unit_price: Number(it.unit_price),
    batch_no: '',
    expiry_date: '',
    remarks: '',
  })));

}, [poItems]);
  const createMut = useMutation({
    mutationFn: (data) => mrnApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries(['mrns']); qc.invalidateQueries(['purchase-orders']);
      qc.invalidateQueries(['dashboard']);
      toast.success('MRN created successfully'); onClose();
    },
    onError: (e) => toast.error(e.error || 'Failed to create MRN'),
  });

  const pickPO = async (po) => {
    setPO(po);
    const res = await mrnApi.nextNumber();
    setMrnNum(res.mrn_number);
    setStep(2);
  };

  const setLine = (i, k, v) => setLines(l => l.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln));

  const submit = (e) => {
    e.preventDefault();
    if (!lines.some(l => l.received_qty > 0)) return toast.error('Enter received quantity for at least one item');
    createMut.mutate({
      mrn_number: mrnNum, po_id: selectedPO.id,
      received_date: form.received_date, received_by: form.received_by, remarks: form.remarks,
      items: lines.filter(l => l.received_qty > 0).map(l => ({
        item_id: l.item_id, po_item_id: l.po_item_id,
        ordered_qty: l.ordered_qty, received_qty: Number(l.received_qty),
        rejected_qty: Number(l.rejected_qty || 0), unit_price: Number(l.unit_price),
        batch_no: l.batch_no, expiry_date: l.expiry_date || null, remarks: l.remarks,
      })),
    });
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg" style={{ width: step === 1 ? 560 : 820 }}>
        <div className="modal-header">
          <ClipboardList size={16} />
          <h2>{step === 1 ? 'Select Purchase Order' : `New MRN against ${selectedPO?.po_number}`}</h2>
          {step === 2 && <span className="badge badge-pending" style={{ marginLeft: 6 }}>Draft</span>}
          <div style={{ flex: 1 }} />
          {step === 2 && <button className="btn btn-sm" onClick={() => setStep(1)}>← Back</button>}
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>

        {/* Step 1: Pick PO */}
        {step === 1 && (
          <div className="modal-body">
            <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 16 }}>
              Select an open Purchase Order to receive materials against:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
              {openPOs.length === 0 && <div className="empty-state"><p>No open POs available</p></div>}
              {openPOs.map(po => (
                <button key={po.id} onClick={() => pickPO(po)} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px', border: '1px solid var(--border)', borderRadius: 8,
                  background: '#fff', cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'left',
                  transition: 'all 0.12s',
                }} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }} className="mono">{po.po_number}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>{po.vendor_name} · {new Date(po.order_date).toLocaleDateString('en-IN')}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span className={`badge badge-${po.status}`}>{po.status}</span>
                    <div style={{ fontSize: 12, marginTop: 4, fontFamily: 'var(--mono)' }}>₹{Number(po.total_amount).toLocaleString('en-IN')}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Fill MRN */}
        {step === 2 && (
          <form onSubmit={submit}>
            <div className="modal-body">
              {/* Header fields */}
              <div className="form-grid" style={{ marginBottom: 20 }}>
                <div className="form-group">
                  <label className="form-label">MRN Number *</label>
                  <input className="form-control mono" value={mrnNum} onChange={e => setMrnNum(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Received Date *</label>
                  <input className="form-control" type="date" value={form.received_date} onChange={e => setForm(f => ({ ...f, received_date: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Received By</label>
                  <input className="form-control" value={form.received_by} onChange={e => setForm(f => ({ ...f, received_by: e.target.value }))} placeholder="Store keeper name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Remarks</label>
                  <input className="form-control" value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} placeholder="Optional" />
                </div>
              </div>

              <div className="section-label">Items to Receive</div>
              {loadingItems ? <div className="spinner" /> : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="line-items-table" style={{ minWidth: 700 }}>
                    <thead><tr>
                      <th style={{ width: '22%' }}>Item</th>
                      <th style={{ width: '10%' }}>PO Qty</th>
                      <th style={{ width: '10%' }}>Pending</th>
                      <th style={{ width: '10%' }}>Received</th>
                      <th style={{ width: '10%' }}>Rejected</th>
                      <th style={{ width: '10%' }}>Unit Price</th>
                      <th style={{ width: '13%' }}>Batch No</th>
                      <th style={{ width: '15%' }}>Expiry</th>
                    </tr></thead>
                    <tbody>
                      {lines.map((ln, i) => (
                        <tr key={i}>
                          <td>
                            <div style={{ fontWeight: 500, fontSize: 12 }}>{ln.item_name}</div>
                            <div className="mono" style={{ fontSize: 11, color: 'var(--gray-400)' }}>{ln.item_code} · {ln.unit}</div>
                          </td>
                          <td style={{ textAlign: 'center', color: 'var(--gray-400)' }}>{ln.ordered_qty}</td>
                          <td style={{ textAlign: 'center', fontWeight: 600, color: ln.remaining_qty > 0 ? 'var(--amber)' : 'var(--accent)' }}>{ln.remaining_qty}</td>
                          <td>
                            <input type="number" min="0" max={ln.remaining_qty}
                              value={ln.received_qty}
                              style={{ borderColor: ln.received_qty > ln.remaining_qty ? 'var(--red)' : undefined }}
                              onChange={e => setLine(i, 'received_qty', e.target.value)} />
                          </td>
                          <td>
                            <input type="number" min="0" max={ln.received_qty}
                              value={ln.rejected_qty}
                              style={{ borderColor: 'var(--red)' }}
                              onChange={e => setLine(i, 'rejected_qty', e.target.value)} />
                          </td>
                          <td>
                            <input type="number" min="0" step="0.01" value={ln.unit_price}
                              onChange={e => setLine(i, 'unit_price', e.target.value)} />
                          </td>
                          <td><input type="text" value={ln.batch_no} placeholder="Optional" onChange={e => setLine(i, 'batch_no', e.target.value)} /></td>
                          <td><input type="date" value={ln.expiry_date} onChange={e => setLine(i, 'expiry_date', e.target.value)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Summary */}
              <div style={{ marginTop: 14, padding: '12px 16px', background: 'var(--accent-light)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--accent-text)', fontWeight: 500 }}>Total Accepted Value</span>
                <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16, color: 'var(--accent-text)' }}>
                  ₹{lines.reduce((s, l) => s + ((Number(l.received_qty) - Number(l.rejected_qty || 0)) * Number(l.unit_price)), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={createMut.isPending}>
                {createMut.isPending ? 'Creating…' : 'Create MRN'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ── Main Page ──────────────────────────────────────────────── */
export default function MRNPage() {
  const qc = useQueryClient();
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatus] = useState('');
  const [viewId, setViewId]     = useState(null);
  const [creating, setCreating] = useState(false);

  const { data: mrns = [], isLoading } = useQuery({
    queryKey: ['mrns', search, statusFilter],
    queryFn: () => mrnApi.getAll({ search, status: statusFilter }),
  });

  const deleteMut = useMutation({
    mutationFn: mrnApi.delete,
    onSuccess: () => { qc.invalidateQueries(['mrns']); toast.success('MRN deleted'); },
    onError: (e) => toast.error(e.error || 'Cannot delete'),
  });

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">Material Received Notes</span>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          <Plus size={14} /> New MRN
        </button>
      </div>
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <ClipboardList size={15} />
            <span className="card-title">MRN Register ({mrns.length})</span>
            <div className="search-wrap">
              <Search />
              <input placeholder="Search MRN, PO, vendor…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="filter-select" value={statusFilter} onChange={e => setStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="table-wrap">
            {isLoading ? <div className="spinner" /> : (
              <table>
                <thead><tr>
                  <th>MRN No.</th><th>PO No.</th><th>Vendor</th><th>Received Date</th>
                  <th>Received By</th><th>Items</th><th>Total Value</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {mrns.length === 0 && (
                    <tr><td colSpan={9}>
                      <div className="empty-state"><ClipboardList /><p>No MRNs found. Create one against a Purchase Order.</p></div>
                    </td></tr>
                  )}
                  {mrns.map(m => (
                    <tr key={m.id}>
                      <td className="mono">{m.mrn_number}</td>
                      <td className="mono">{m.po_number}</td>
                      <td style={{ fontWeight: 500 }}>{m.vendor_name}</td>
                      <td>{fmtDate(m.received_date)}</td>
                      <td>{m.received_by || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{m.line_count}</td>
                      <td className="amount">₹{Number(m.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td><span className={`badge badge-${m.status}`}>{m.status}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-icon" title="View" onClick={() => setViewId(m.id)}><Eye /></button>
                          {/* {m.status !== 'approved' && (
                            <button className="btn-icon danger" onClick={() => { if (window.confirm('Delete MRN?')) deleteMut.mutate(m.id); }}><Trash2 /></button>
                          )}*/}
                        </div> 
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {viewId && <ViewMRN mrnId={viewId} onClose={() => setViewId(null)} />}
      {creating && <CreateMRN onClose={() => setCreating(false)} />}
    </>
  );
}